import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import Header from '@components/Header/Header'
import ProviderLabel from '@components/ProviderLabel/ProviderLabel'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import {
  Container,
  ContentContainer,
  ProviderSection,
  AmountSection,
  AmountLabel,
  AmountValue,
  ButtonWrapper
} from './PaymentConfirm.styles'
import { NEUTRAL_100, WHITE } from '@styles/colors'
import { PpobProduct } from '@hooks/usePpobProducts'
import { setHeaderColor } from '@lib/telegram'

type PaymentConfirmProps = {
  providerIconUrl?: string
  providerName?: string
  showPostpaid?: boolean
  amount?: number
  onContinue?: () => void
}

const PaymentConfirm = ({
  providerIconUrl: providerIconUrlProp,
  providerName: providerNameProp,
  amount: amountProp,
  onContinue
}: PaymentConfirmProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const state = location.state as {
    phoneNumber?: string
    providerName?: string
    providerImageUrl?: string | null
    amount?: number
    amountNet?: number
    productAmount?: string
    product?: PpobProduct
    service?: string
    accountNumber?: string
    customerName?: string
    customerDetails?: {
      name: string
      segment_power: string
      meter_no: string
      subscriber_id: string
    }
    paymentType?: 'prepaid' | 'postpaid'
    refId?: string
    customerNo?: string
    buyerSkuCode?: string
    inquiryQuote?: string
    adminFee?: number
    platformFee?: number
  } | undefined

  // Use route state if available, otherwise use props
  const service = state?.service
  const isPln = service === 'pln'
  const providerName = isPln ? 'PLN' : (state?.providerName || providerNameProp)
  const providerIconUrl = isPln ? 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/other_providers/PLN.svg' : (state?.providerImageUrl || providerIconUrlProp)
  const amount = state?.amount || amountProp || 0
  const amountNet = state?.amountNet || state?.amount || amountProp || 0

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  const handleContinue = () => {
    if (onContinue) {
      onContinue()
    } else {
      navigate('/payment-summary', {
        state: {
          phoneNumber: state?.phoneNumber,
          providerName,
          providerImageUrl: providerIconUrl,
          amount,
          amountNet: state?.amountNet,
          product: state?.product,
          service: state?.service,
          accountNumber: state?.accountNumber,
          customerName: state?.customerName,
          customerDetails: state?.customerDetails,
          paymentType: state?.paymentType,
          refId: state?.refId,
          customerNo: state?.customerNo,
          buyerSkuCode: state?.buyerSkuCode,
          inquiryQuote: state?.inquiryQuote,
          adminFee: state?.adminFee,
          platformFee: state?.platformFee,
          productAmount: state?.productAmount
        }
      })
    }
  }

  return (
    <Container>
      <Header
        title={t('paymentConfirm.header')}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
      />
      <ContentContainer>
        <ProviderSection>
          <ProviderLabel
            providerIconUrl={providerIconUrl}
            providerName={providerName}
          />
        </ProviderSection>
        <AmountSection>
          <AmountLabel>{t('paymentConfirm.amountToPay')}</AmountLabel>
          <AmountValue>{amountNet.toLocaleString('id-ID')} JIDR</AmountValue>
        </AmountSection>
        <div
          style={{
            width: '100%',
            height: 1,
            backgroundColor: NEUTRAL_100
          }}
        />
      </ContentContainer>
      <ButtonWrapper>
        <PrimaryButton onClick={handleContinue}>
          {t('common.continue')}
        </PrimaryButton>
      </ButtonWrapper>
    </Container>
  )
}

export default PaymentConfirm
